import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import {
  DirectionsRun as RunIcon,
  Pool as CrossTrainingIcon,
  CalendarToday as CalendarIcon,
  Bedtime as SleepIcon,
  LocalDrink as HydrationIcon,
  Restaurant as NutritionIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { ActivityFormData, ActivitySubmissionResult, DailyActivity } from '../../types';
import { activityService } from '../../services/activityService';
import { minutesToHMS, minutesFromUserParts, normalizeMinutesForStorage } from '../../utils/prTimeFormat';
import { useAuth } from '../../context/AuthContext';
import ActivitySummaryModal from './ActivitySummaryModal';

const MAX_MILES_PER_ENTRY = 30;
const MILES_FIELDS: Array<keyof ActivityFormData> = ['milesRun', 'warmupMiles', 'workoutMiles', 'cooldownMiles'];

const clampMilesValue = (value: number): number => Math.min(MAX_MILES_PER_ENTRY, Math.max(0, value));

/** Map legacy workout labels to form value `Hard`. */
function normalizeWorkoutTypeForForm(
  w: DailyActivity['workoutType'] | undefined
): ActivityFormData['workoutType'] | undefined {
  if (!w) return undefined;
  if (w === 'VO2 Max' || w === 'Hard workout') return 'Hard';
  return w;
}

interface RunningActivityModalProps {
  open: boolean;
  onClose: () => void;
  onActivitySubmitted: (result: ActivitySubmissionResult, navigateToProfile?: () => void) => void;
  teamId: string;
  editingActivity?: DailyActivity | null;
  healthMetricsData?: Partial<ActivityFormData> & { _submissionDate?: string }; // Pre-filled health metrics; _submissionDate = date used
}

const RunningActivityModal: React.FC<RunningActivityModalProps> = ({
  open,
  onClose,
  onActivitySubmitted,
  teamId,
  editingActivity = null,
  healthMetricsData
}) => {
  const { user } = useAuth();
  const isAthlete = user?.role === 'athlete';
  const editingIsRaceDay = editingActivity?.runSubType === 'Race Day';
  const showRaceDayOption = !isAthlete || editingIsRaceDay;
  const disableRaceDayOption = isAthlete && editingIsRaceDay;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [submissionResult, setSubmissionResult] = useState<ActivitySubmissionResult | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [showNoActivityConfirmDialog, setShowNoActivityConfirmDialog] = useState(false);

  // Form data state - only running activities
  const [formData, setFormData] = useState<ActivityFormData>({
    sleepScore: 0,
    hydrationLevel: 0,
    nutritionLevel: 0,
    runType: 'None',
    runSubType: undefined,
    workoutType: undefined,
    crossTrainingType: undefined,
    milesRun: 0,
    warmupMiles: 0,
    workoutMiles: 0,
    cooldownMiles: 0,
    crossTrainingMinutes: 0,
    strengthMinutes: 0,
  });
  
  // Race-specific fields (stored separately as they're not in ActivityFormData)
  const [raceEventType, setRaceEventType] = useState<string>('5K');
  const [raceTimeHours, setRaceTimeHours] = useState<string>('');
  const [raceTimeMinutes, setRaceTimeMinutes] = useState<string>('');
  const [raceTimeSeconds, setRaceTimeSeconds] = useState<string>('');

  // Function to convert race event type to miles
  const getRaceDistanceInMiles = (eventType: string): number => {
    const distanceMap: { [key: string]: number } = {
      '400m': 0.25,
      '800m': 0.5,
      '1600m': 1.0,
      '3200m': 2.0,
      '3k': 1.86,
      '5K': 3.1,
      '10K': 6.2,
      'Half Marathon': 13.1,
      'Marathon': 26.2,
    };
    return distanceMap[eventType] || 0;
  };

  // Auto-calculate miles when race event type changes and Race Day is selected
  // Clear miles when changing away from Race Day
  useEffect(() => {
    if (formData.runSubType === 'Race Day') {
      const miles = getRaceDistanceInMiles(raceEventType);
      setFormData(prev => ({
        ...prev,
        milesRun: miles
      }));
    } else if (formData.runSubType === 'Easy' || formData.runSubType === 'Long') {
      // Clear miles when switching from Race Day to Easy or Long
      setFormData(prev => ({
        ...prev,
        milesRun: 0
      }));
    }
  }, [raceEventType, formData.runSubType]);

  // Clear miles when not a Run type that uses the single miles field, or when Run subtype is Workout (split miles)
  useEffect(() => {
    if (formData.runType !== 'Run' && formData.runType !== 'Workout') {
      setFormData(prev => ({ ...prev, milesRun: 0 }));
      return;
    }
    if (formData.runType === 'Run' && formData.runSubType === 'Workout') {
      setFormData(prev => ({ ...prev, milesRun: 0 }));
    }
  }, [formData.runType, formData.runSubType]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      if (editingActivity) {
        const legacyWorkout = editingActivity.runType === 'Workout';
        const runType = legacyWorkout ? 'Run' : (editingActivity.runType || 'None');
        const runSubType = legacyWorkout ? 'Workout' : editingActivity.runSubType;
        setFormData({
          sleepScore: editingActivity.sleepScore ?? 0,
          hydrationLevel: editingActivity.hydrationLevel ?? 0,
          nutritionLevel: editingActivity.nutritionLevel ?? 0,
          runType,
          runSubType,
          workoutType: normalizeWorkoutTypeForForm(editingActivity.workoutType),
          crossTrainingType: editingActivity.crossTrainingType,
          milesRun: editingActivity.milesRun ?? 0,
          warmupMiles: editingActivity.warmupMiles ?? 0,
          workoutMiles: editingActivity.workoutMiles ?? 0,
          cooldownMiles: editingActivity.cooldownMiles ?? 0,
          crossTrainingMinutes: editingActivity.crossTrainingMinutes ?? 0,
          strengthMinutes: editingActivity.strengthMinutes ?? 0,
        });
        // Load race fields if this is a Race Day activity
        if (editingActivity.runSubType === 'Race Day' && editingActivity.raceTime) {
          setRaceEventType(editingActivity.raceEventType || '5K');
          const totalMinutes = editingActivity.raceTime;
          const { h, m, s } = minutesToHMS(Number(totalMinutes));
          setRaceTimeHours(h > 0 ? String(h) : '');
          setRaceTimeMinutes(String(m));
          setRaceTimeSeconds(s !== 0 ? String(s) : '');
        } else {
          setRaceEventType('5K');
          setRaceTimeHours('');
          setRaceTimeMinutes('');
          setRaceTimeSeconds('');
        }
        // Set date to the activity's date when editing
        setSelectedDate(editingActivity.date);
      } else {
        // Reset to default values for new activity
        const { _submissionDate, ...healthData } = healthMetricsData || {};
        setFormData({
          sleepScore: healthData?.sleepScore || 0,
          hydrationLevel: healthData?.hydrationLevel || 0,
          nutritionLevel: healthData?.nutritionLevel || 0,
          runType: 'None',
          runSubType: undefined,
          workoutType: undefined,
          crossTrainingType: undefined,
          milesRun: 0,
          warmupMiles: 0,
          workoutMiles: 0,
          cooldownMiles: 0,
          crossTrainingMinutes: 0,
          strengthMinutes: 0,
        });
        // Reset race fields
        setRaceEventType('5K');
        setRaceTimeHours('');
        setRaceTimeMinutes('');
        setRaceTimeSeconds('');
        // Use date from Health Metrics flow when present, otherwise today
        setSelectedDate(_submissionDate || activityService.getTodayDateString());
      }
      setError('');
      setShowSummary(false);
      setSubmissionResult(null);
    }
  }, [open, editingActivity, healthMetricsData]);

  const handleHealthStepperChange = (field: 'sleepScore' | 'hydrationLevel' | 'nutritionLevel', delta: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: Math.min(10, Math.max(0, prev[field] + delta)),
    }));
  };

  const handleRunTypeChange = (event: any) => {
    const value = event.target.value as ActivityFormData['runType'];
    setFormData(prev => ({
      ...prev,
      runType: value,
      runSubType: undefined,
      workoutType: undefined,
      crossTrainingType: undefined,
      milesRun: 0,
      warmupMiles: 0,
      workoutMiles: 0,
      cooldownMiles: 0,
      crossTrainingMinutes: 0,
      strengthMinutes: 0,
    }));
  };

  const handleInputChange = (field: keyof ActivityFormData) => (event: any) => {
    const value = event.target.value;
    const isMilesField = MILES_FIELDS.includes(field);
    setFormData(prev => ({
      ...prev,
      [field]:
        field.includes('Minutes') || field === 'milesRun' || field.includes('Miles')
          ? (() => {
              const parsedValue = parseFloat(value);
              if (!Number.isFinite(parsedValue)) return 0;
              if (isMilesField) return clampMilesValue(parsedValue);
              return Math.max(0, parsedValue);
            })()
          : value
    }));
  };

  // Handle focus on number inputs - clear 0 values for better UX
  const handleNumberFocus = (field: keyof ActivityFormData) => (event: React.FocusEvent<HTMLInputElement>) => {
    const currentValue = formData[field];
    // Only check for number 0 (not string '0') since these are number fields
    if (typeof currentValue === 'number' && currentValue === 0) {
      // Clear the value by setting it to empty string in the input
      // This will trigger onChange which will handle the state update
      const input = event.target;
      input.value = '';
      // Manually trigger input event to update React state
      const changeEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(changeEvent);
    }
  };

  const handleSubmitClick = () => {
    if (formData.runType === 'None') {
      setShowNoActivityConfirmDialog(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!user) return;

    console.log('[RunningActivityModal] Starting handleSubmit');
    setLoading(true);
    setError('');

    try {
      let result;
      let activityId: string | null = null;
      
      if (editingActivity) {
        console.log('[RunningActivityModal] Updating existing activity:', editingActivity.id);
        result = await activityService.updateDailyActivity(editingActivity.id, formData);
        activityId = editingActivity.id;
      } else {
        console.log('[RunningActivityModal] Submitting new activity');
        
        // Add timeout to prevent hanging on iOS
        const submitPromise = activityService.submitDailyActivity(user.id, teamId, formData, selectedDate || undefined);
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Activity submission timed out')), 10000); // 10 second timeout (REST API needs more time)
        });
        
        try {
          result = await Promise.race([submitPromise, timeoutPromise]) as any;
          console.log('[RunningActivityModal] Activity submission result:', result);
        } catch (timeoutError: any) {
          console.warn('[RunningActivityModal] Activity submission timed out, but activity may have been created');
          // On iOS, the activity is usually created even if the promise times out
          // Set a basic success result
          result = {
            success: true,
            message: 'Activity logged successfully',
            pointsEarned: 1.0, // Default points
            submissionTiming: 'same_day'
          };
        }
        
        // Skip fetching activity ID on iOS to avoid another timeout
        // The summary modal doesn't strictly need it
        if (result.success) {
          console.log('[RunningActivityModal] Activity submitted successfully');
          // Don't fetch activities on iOS - it's not critical and can timeout
          // activityId will remain null, which is fine for showing summary
        }
      }
      
      // If Race Day is selected, save race-specific fields
      // Skip this on iOS if we don't have activityId
      if (result.success && formData.runSubType === 'Race Day' && activityId && 
          (raceTimeHours || raceTimeMinutes || raceTimeSeconds)) {
        console.log('[RunningActivityModal] Saving race-specific fields');
        const totalMinutes = normalizeMinutesForStorage(
          minutesFromUserParts(
            Number(raceTimeHours) || 0,
            Number(raceTimeMinutes) || 0,
            Number(raceTimeSeconds) || 0
          )
        );
        
        // Check platform
        const { Capacitor } = await import('@capacitor/core');
        const platform = Capacitor.getPlatform();
        
        if (platform === 'ios') {
          console.log('[RunningActivityModal] iOS detected - skipping race field update to avoid hang');
          // On iOS, skip the update to avoid hanging
          // The race fields would need to be added via REST API, but it's not critical
        } else {
          // Update activity with race fields via Firestore on web/Android
          const { doc, updateDoc } = await import('firebase/firestore');
          const { db } = await import('../../firebase/config');
          const activityRef = doc(db, 'dailyActivities', activityId);
          await updateDoc(activityRef, {
            raceEventType: raceEventType,
            raceTime: totalMinutes,
          });
        }
      }
      
      if (result.success) {
        console.log('[RunningActivityModal] Activity submitted successfully, showing summary modal');
        setSubmissionResult(result);
        setShowSummary(true);
      } else {
        console.log('[RunningActivityModal] Activity submission failed:', result.message);
        setError(result.message);
      }
    } catch (err: any) {
      console.error('[RunningActivityModal] Error in handleSubmit:', err);
      console.error('[RunningActivityModal] Error message:', err.message);
      setError(err.message || `Failed to ${editingActivity ? 'update' : 'submit'} running activity`);
    } finally {
      setLoading(false);
    }
  };

  const renderRunningActivity = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
        {editingActivity ? 'Edit health metrics and activity:' : 'Tell us about your activity:'}
      </Typography>

      {/* Date Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Date</Typography>
          </Box>
          <TextField
            fullWidth
            type="date"
            label="Select the date for this activity"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            disabled={editingActivity !== null} // Disable date change when editing existing activity
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: activityService.getTodayDateString(), // Prevent future dates
            }}
            helperText={
              editingActivity 
                ? "Date cannot be changed when editing an existing activity" 
                : "Choose the date when this activity occurred (defaults to today)"
            }
          />
        </CardContent>
      </Card>

      {/* Activity Type - moved to top so users see it first */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <RunIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Activity Type</Typography>
          </Box>
          <FormControl fullWidth>
            <InputLabel>What type of activity did you do?</InputLabel>
            <Select
              value={formData.runType}
              onChange={handleRunTypeChange}
              label="What type of activity did you do?"
            >
              <MenuItem value="None">Please select an option!</MenuItem>
              <MenuItem value="Run">Run</MenuItem>
              <MenuItem value="Strength">Strength</MenuItem>
              <MenuItem value="Cross Training">Cross Training</MenuItem>
              <MenuItem value="Rest Day">Rest</MenuItem>
              <MenuItem value="Sick">Sick</MenuItem>
              <MenuItem value="Injured">Injured</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Run Sub Type - Only show if Run is selected */}
      {formData.runType === 'Run' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Run Sub Type
            </Typography>
            <FormControl fullWidth>
              <InputLabel>What type of run was it?</InputLabel>
              <Select
                value={formData.runSubType || ''}
                onChange={handleInputChange('runSubType')}
                label="What type of run was it?"
              >
                <MenuItem value="Easy">Easy</MenuItem>
                <MenuItem value="Long">Long</MenuItem>
                <MenuItem value="Workout">Workout</MenuItem>
                {showRaceDayOption && (
                  <MenuItem value="Race Day" disabled={disableRaceDayOption}>
                    Race Day
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* Race Details - Only show if Race Day is selected */}
      {formData.runSubType === 'Race Day' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Race Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={raceEventType}
                  onChange={(e) => setRaceEventType(e.target.value)}
                  label="Event Type"
                >
                  <MenuItem value="400m">400m</MenuItem>
                  <MenuItem value="800m">800m</MenuItem>
                  <MenuItem value="1600m">1600m (Mile)</MenuItem>
                  <MenuItem value="3200m">3200m (2 Mile)</MenuItem>
                  <MenuItem value="3k">3k</MenuItem>
                  <MenuItem value="5K">5K</MenuItem>
                  <MenuItem value="10K">10K</MenuItem>
                  <MenuItem value="Half Marathon">Half Marathon</MenuItem>
                  <MenuItem value="Marathon">Marathon</MenuItem>
                </Select>
              </FormControl>
              <Box>
                <Typography variant="body2" gutterBottom>
                  Race Time
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="Hours"
                    type="number"
                    value={raceTimeHours}
                    onChange={(e) => setRaceTimeHours(e.target.value)}
                    inputProps={{ min: 0, max: 23 }}
                    sx={{ width: 100 }}
                  />
                  <Typography>:</Typography>
                  <TextField
                    label="Minutes"
                    type="number"
                    value={raceTimeMinutes}
                    onChange={(e) => setRaceTimeMinutes(e.target.value)}
                    inputProps={{ min: 0, max: 59 }}
                    sx={{ width: 100 }}
                  />
                  <Typography>:</Typography>
                  <TextField
                    label="Seconds"
                    type="number"
                    value={raceTimeSeconds}
                    onChange={(e) => setRaceTimeSeconds(e.target.value)}
                    inputProps={{ min: 0, max: 59 }}
                    sx={{ width: 100 }}
                  />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Workout intensity — Run + Workout subtype */}
      {formData.runType === 'Run' && formData.runSubType === 'Workout' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Workout type
            </Typography>
            <FormControl fullWidth>
              <InputLabel>What type of workout was it?</InputLabel>
              <Select
                value={formData.workoutType || ''}
                onChange={handleInputChange('workoutType')}
                label="What type of workout was it?"
              >
                <MenuItem value="Tempo">Tempo</MenuItem>
                <MenuItem value="Threshold">Threshold</MenuItem>
                <MenuItem value="Hard">Hard</MenuItem>
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* Cross Training Type - Only show if Cross Training is selected */}
      {formData.runType === 'Cross Training' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CrossTrainingIcon sx={{ mr: 1, color: 'info.main' }} />
              <Typography variant="h6">Cross Training Type</Typography>
            </Box>
            <FormControl fullWidth>
              <InputLabel>What type of cross training did you do?</InputLabel>
              <Select
                value={formData.crossTrainingType || ''}
                onChange={handleInputChange('crossTrainingType')}
                label="What type of cross training did you do?"
              >
                <MenuItem value="Bike">Bike</MenuItem>
                <MenuItem value="Swim">Swim</MenuItem>
                <MenuItem value="Walk">Walk</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* Miles Run — Run with Easy / Long / Race Day (not Workout subtype) */}
      {formData.runType === 'Run' &&
        formData.runSubType &&
        formData.runSubType !== 'Workout' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Miles Run
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="How many miles did you run?"
              value={formData.milesRun === 0 ? '' : formData.milesRun}
              onChange={handleInputChange('milesRun')}
              onFocus={handleNumberFocus('milesRun')}
              inputProps={{ min: 0, max: MAX_MILES_PER_ENTRY, step: 0.1 }}
              disabled={formData.runSubType === 'Race Day'}
              helperText={
                formData.runSubType === 'Race Day' 
                  ? `Auto-calculated based on race distance (${formData.milesRun} miles)` 
                  : `Enter 0-${MAX_MILES_PER_ENTRY} miles`
              }
            />
          </CardContent>
        </Card>
      )}

      {/* Split workout miles — Run + Workout subtype */}
      {formData.runType === 'Run' && formData.runSubType === 'Workout' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Workout miles
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Warm Up Miles (Easy)"
                value={formData.warmupMiles === 0 ? '' : formData.warmupMiles}
                onChange={handleInputChange('warmupMiles')}
                onFocus={handleNumberFocus('warmupMiles')}
                inputProps={{ min: 0, max: MAX_MILES_PER_ENTRY, step: 0.1 }}
                helperText={`Easy pace warm up miles (0-${MAX_MILES_PER_ENTRY})`}
              />
              <TextField
                fullWidth
                type="number"
                label="Workout Miles"
                value={formData.workoutMiles === 0 ? '' : formData.workoutMiles}
                onChange={handleInputChange('workoutMiles')}
                onFocus={handleNumberFocus('workoutMiles')}
                inputProps={{ min: 0, max: MAX_MILES_PER_ENTRY, step: 0.1 }}
                helperText={`Main workout miles at target pace (0-${MAX_MILES_PER_ENTRY})`}
              />
              <TextField
                fullWidth
                type="number"
                label="Cooldown Miles (Easy)"
                value={formData.cooldownMiles === 0 ? '' : formData.cooldownMiles}
                onChange={handleInputChange('cooldownMiles')}
                onFocus={handleNumberFocus('cooldownMiles')}
                inputProps={{ min: 0, max: MAX_MILES_PER_ENTRY, step: 0.1 }}
                helperText={`Easy pace cooldown miles (0-${MAX_MILES_PER_ENTRY})`}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Strength minutes */}
      {formData.runType === 'Strength' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Strength (minutes)
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="How many minutes of strength work?"
              value={formData.strengthMinutes === 0 ? '' : formData.strengthMinutes}
              onChange={handleInputChange('strengthMinutes')}
              onFocus={handleNumberFocus('strengthMinutes')}
              inputProps={{ min: 0, step: 1 }}
              helperText="Total minutes of strength training"
            />
          </CardContent>
        </Card>
      )}

      {/* Cross Training Minutes - Only show if Cross Training is selected */}
      {formData.runType === 'Cross Training' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Total Minutes of Activity
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="How many minutes did you do this activity?"
              value={formData.crossTrainingMinutes === 0 ? '' : formData.crossTrainingMinutes}
              onChange={handleInputChange('crossTrainingMinutes')}
              onFocus={handleNumberFocus('crossTrainingMinutes')}
              inputProps={{ min: 0, step: 1 }}
              helperText="Enter the total minutes of cross training activity"
            />
          </CardContent>
        </Card>
      )}

      {/* Rest Day - Only show if Rest Day is selected */}
      {formData.runType === 'Rest Day' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" color="text.secondary">
                🛌 Rest Day
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              You've selected a rest day. No additional activity tracking needed.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Sick - Only show if Sick is selected */}
      {formData.runType === 'Sick' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" color="text.secondary">
                🤒 Sick
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              You've selected sick day. Rest and recover - no additional activity tracking needed.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Injured - Only show if Injured is selected */}
      {formData.runType === 'Injured' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" color="text.secondary">
                🩹 Injured
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              You've selected injured day. Focus on recovery - no additional activity tracking needed.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Health Metrics - moved to bottom */}
      <Card sx={{ mb: 0, borderLeft: '4px solid', borderColor: 'primary.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SleepIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Health Metrics</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editingActivity ? 'Update your sleep, hydration, and nutrition for this day.' : 'Optionally add how you felt (1–10) for this day.'}
          </Typography>
          <Box sx={{ px: 0.5, mb: 2 }}>
            <Typography variant="body2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SleepIcon fontSize="small" color="primary" /> Sleep
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => handleHealthStepperChange('sleepScore', -1)} disabled={formData.sleepScore <= 0} aria-label="Decrease sleep">
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 32, textAlign: 'center', fontWeight: 600 }}>{formData.sleepScore}/10</Typography>
              <IconButton size="small" onClick={() => handleHealthStepperChange('sleepScore', 1)} disabled={formData.sleepScore >= 10} aria-label="Increase sleep">
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ px: 0.5, mb: 2 }}>
            <Typography variant="body2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <HydrationIcon fontSize="small" color="info" /> Hydration
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => handleHealthStepperChange('hydrationLevel', -1)} disabled={formData.hydrationLevel <= 0} aria-label="Decrease hydration">
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 32, textAlign: 'center', fontWeight: 600 }}>{formData.hydrationLevel}/10</Typography>
              <IconButton size="small" onClick={() => handleHealthStepperChange('hydrationLevel', 1)} disabled={formData.hydrationLevel >= 10} aria-label="Increase hydration">
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ px: 0.5 }}>
            <Typography variant="body2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <NutritionIcon fontSize="small" color="success" /> Nutrition
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => handleHealthStepperChange('nutritionLevel', -1)} disabled={formData.nutritionLevel <= 0} aria-label="Decrease nutrition">
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 32, textAlign: 'center', fontWeight: 600 }}>{formData.nutritionLevel}/10</Typography>
              <IconButton size="small" onClick={() => handleHealthStepperChange('nutritionLevel', 1)} disabled={formData.nutritionLevel >= 10} aria-label="Increase nutrition">
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const handleNavigateToDashboard = () => {
    // Close the modal
    setShowSummary(false);
    onClose();
    // Call onActivitySubmitted with navigation callback to activities page
    // The parent (App.tsx) will handle the actual navigation
    if (submissionResult) {
      onActivitySubmitted(submissionResult);
    }
  };

  const handleCloseSummary = () => {
    setShowSummary(false);
    onClose();
  };

  const handleCloseModal = () => {
    onClose();
  };

  return (
    <>
      <Dialog
        open={open && !showSummary}
        onClose={loading ? undefined : handleCloseModal}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={loading}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ textAlign: 'center', flex: 2 }}>
              <Typography variant="h5" gutterBottom>
                {editingActivity ? 'Edit Activity' : 'Activity Log'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={handleCloseModal}
                disabled={loading}
                sx={{ minWidth: 'auto', p: 1 }}
                color="inherit"
              >
                ✕
              </Button>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ minHeight: 400 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {renderRunningActivity()}
        </DialogContent>

        <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
          <Button
            onClick={handleCloseModal}
            disabled={loading}
            color="inherit"
          >
            Close
          </Button>
          <Button
            onClick={handleSubmitClick}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? (editingActivity ? 'Updating...' : 'Submitting...') : (editingActivity ? 'Update' : 'Submit Activity')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* No activity confirmation dialog */}
      <Dialog
        open={showNoActivityConfirmDialog}
        onClose={() => setShowNoActivityConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Submit without activity?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to submit without entering an activity for today? You can always add or edit your activity later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNoActivityConfirmDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowNoActivityConfirmDialog(false);
              handleSubmit();
            }}
            disabled={loading}
          >
            Yes, submit without activity
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activity Summary Modal */}
      {submissionResult && (
        <ActivitySummaryModal
          open={showSummary}
          onClose={handleCloseSummary}
          onNavigateToDashboard={handleNavigateToDashboard}
          submissionResult={submissionResult}
          teamId={teamId}
          userId={user?.id || ''}
        />
      )}
    </>
  );
};

export default RunningActivityModal;
